/* This example requires Tailwind CSS v2.0+ */
import React, { useContext, useEffect, useState } from "react";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { Capability } from "models/Capability.model";
import { OrgContext } from "contexts/OrgContext";
import agent from "server";
import InputField from "components/UI-lib/InputField";
import Button from "components/UI-lib/Button";
import InlineNotification from "contexts/Notifications/InlineNotifications";
import Modal from "components/Modals/Modal";
import Dropdown, { DropdownItem } from "components/UI-lib/Dropdown";
import {
  CreateApplicationRequest,
  RemoveApplicationRequest,
  SupportedServiceType,
} from "server/applications";
import UrlRowItem from "./UrlRowItem";
import DevToggle from "components/DevToggle";
import { getTunnelApplicationHealthCheck } from "components/Environment/utils";
import { validateTunnelName } from "components/utils";
import { Environment } from "components/Environment/utils/types";
import {
  getEnvironmentStatus,
  getHealthChecksFromEnvironment,
  getIngressApplicationsFromEnvironment,
} from "components/Environment/utils/environtmentUtils";
import { WorkspaceStatus } from "models/Workspace.model";
import {
  NodeDetailSectionDescription,
  NodeDetailSectionHeader,
  NodeDetailTable,
  NodeDetailTableBody,
  NodeDetailTableHead,
  NodeDetailTableHeaderCell,
} from "components/NodeDetail/shared/NodeDetailSection";

interface CloudflareHTTPPortsProps {
  environment: Environment;
  capabilities: Capability[];
}

const CloudflareHTTPPorts: React.FC<CloudflareHTTPPortsProps> = (props) => {
  const wsContext = useContext(WorkspaceContext);
  const orgContext = useContext(OrgContext);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingApplication, setCreatingApplication] = useState(false);
  const [environment, setEnvironment] = useState<Environment>(
    props.environment,
  );
  const [name, setName] = useState("");
  const [namePlaceholder, setNamePlaceholer] = useState("");
  const [port, setPort] = useState("");
  const [serviceType, setServiceType] = useState<SupportedServiceType>(
    SupportedServiceType.HTTP,
  );

  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("error");
  const [isDeletingIndex, setIsDeletingIndex] = useState<string | null>(null);

  const [nameError, setNameError] = useState("");
  const environmentStatus = getEnvironmentStatus(environment);
  const isDeletingEnvironment = environmentStatus === WorkspaceStatus.Deleting;

  useEffect(() => {
    if (name === "") {
      setNamePlaceholer(port);
    }
  }, [port]);

  // always reset the workspace from the context
  useEffect(() => {
    setEnvironment(props.environment);
  }, [props.environment]);

  // useEffect hook to close the notification after 7 seconds
  useEffect(() => {
    if (inlineAlertMessage && inlineAlertSeverity === "success") {
      const timer = setTimeout(() => {
        setInlineAlertMessage("");
      }, 7000);

      // Clear timer on unmount
      return () => clearTimeout(timer);
    }
  }, [inlineAlertMessage]);

  const handleAddNewApplication = async () => {
    if (isDeletingEnvironment) {
      return;
    }

    const payload: CreateApplicationRequest = {
      name,
      port: parseInt(port, 10),
      serviceType: serviceType,
    };
    try {
      const response = await agent.Applications.create(
        environment.workspaceId || "",
        payload,
      );

      // check response status
      if (response.success) {
        // success - show success notification
        setInlineAlertMessage(`Application ${name} created successfully.`);
        setInlineAlertSeverity("success");

        setName("");
        setPort("");

        // refresh the applications list
        wsContext.reloadEnvironments(orgContext.activeOrgId);
      } else {
        ("");
        // handle 404 error
        setInlineAlertMessage(
          `Failed to create application. ${response.message}`,
        );
        setInlineAlertSeverity("error");
      }
    } catch (error) {
      console.error(error);
      // error - show error notification
      setInlineAlertMessage(
        "Failed to create application due to a network error.",
      );
      setInlineAlertSeverity("error");
    }
  };

  const deleteApplication = async (applicationName: string) => {
    const payload: RemoveApplicationRequest = {
      applicationName: applicationName,
    };

    try {
      const response = await agent.Applications.remove(
        environment.workspaceId || "",
        payload,
      );
      if (response.success) {
        setInlineAlertMessage(
          `Application ${applicationName} removed successfully.`,
        );
        setInlineAlertSeverity("success");

        // refresh the applications list
        wsContext.reloadEnvironments(orgContext.activeOrgId);
      } else {
        setInlineAlertMessage(
          `Failed to delete application. ${response.message}`,
        );
        setInlineAlertSeverity("error");
      }
    } catch (error) {
      console.error(error);
      setInlineAlertMessage("Failed to delete application.");
      setInlineAlertSeverity("error");
    }
  };

  return (
    <>
      <Modal
        isOpen={showCreateModal}
        setOpen={setShowCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setName("");
          setPort("");
        }}
        onSuccess={async () => {
          setCreatingApplication(true);
          await handleAddNewApplication();
          setCreatingApplication(false);
          setShowCreateModal(false);
        }}
        title={`Share a Service`}
        body={
          <div className="w-[450px] pb-10 dark:bg-zinc-900">
            <p className="text-sm text-gray-500 dark:text-secondary mb-2">
              Expose a service running on a port without having to open a port
              on your firewall.
            </p>
            <div className="flex flex-row mb-2">
              <DevToggle>
                <Dropdown
                  label="Type"
                  required={true}
                  className="mr-3"
                  displayFunction={(dv) => `${dv}`}
                  value={serviceType}
                  onChange={(dv) => {
                    setServiceType(dv);
                  }}
                >
                  {Object.values(SupportedServiceType).map((type, index) => (
                    <DropdownItem
                      displayValue={type}
                      value={type}
                      key={index}
                    />
                  ))}
                </Dropdown>
              </DevToggle>{" "}
              <InputField
                label="Port"
                required={true}
                value={port}
                errorMessage=""
                placeholder="port"
                className="mr-3 w-[60%]"
                onChange={(val) => {
                  if (/^[0-9]*$/.test(val)) {
                    // this regex checks if val is composed only of digits
                    setPort(val);
                    if (name === "" || name === port) setName(val);
                  }
                }}
              />
              <InputField
                label="Name"
                required={true}
                value={name}
                errorMessage={nameError}
                placeholder={namePlaceholder}
                className="mr-3 w-[60%]"
                onChange={(val) => {
                  setName(val);
                  setNameError(validateTunnelName(val));
                }}
              />
            </div>
          </div>
        }
        disableSuccess={
          isDeletingEnvironment ||
          name === "" ||
          port === "" ||
          nameError !== ""
        }
        confirmLabel="Create"
      />

      <>
        <InlineNotification
          show={!!inlineAlertMessage}
          severity={inlineAlertSeverity}
          text={inlineAlertMessage}
          autoClose={false}
          onClose={() => setInlineAlertMessage("")}
          className="mb-3 mt-3"
        />

        <NodeDetailSectionHeader
          trailing={
            <Button
              label="Share a Service"
              type="primary"
              loading={creatingApplication}
              onClick={() => {
                if (!isDeletingEnvironment) setShowCreateModal(true);
              }}
              disabled={creatingApplication || isDeletingEnvironment}
              className=""
            />
          }
        >
          <NodeDetailSectionDescription className="mt-4 text-primary">
            Access any http application protected with your login; share it with
            teammates, or the public.{" "}
            <a
              className="underline"
              href="https://brev.dev/docs/how-to/deployments"
              target="_blank"
              rel="noreferrer"
            >
              Docs here.
            </a>
          </NodeDetailSectionDescription>
        </NodeDetailSectionHeader>

        <NodeDetailTable className="mb-1">
          <NodeDetailTableHead>
            <tr>
              <NodeDetailTableHeaderCell>Port</NodeDetailTableHeaderCell>
              <NodeDetailTableHeaderCell>
                Shareable URL
              </NodeDetailTableHeaderCell>
              <NodeDetailTableHeaderCell>Health</NodeDetailTableHeaderCell>
              <NodeDetailTableHeaderCell className="dark:text-gray-300">
                {/* Actions */}
              </NodeDetailTableHeaderCell>
            </tr>
          </NodeDetailTableHead>
          <NodeDetailTableBody>
            {environment.instance?.firewall_rules &&
              (() => {
                const applications =
                  getIngressApplicationsFromEnvironment(environment);
                const healthChecks =
                  getHealthChecksFromEnvironment(environment);
                return applications.map((app, index) => (
                  <UrlRowItem
                    key={index}
                    isDisabled={
                      isDeletingEnvironment ||
                      (isDeletingIndex !== null && isDeletingIndex !== app.name)
                    }
                    isLoading={environmentStatus === WorkspaceStatus.Deploying}
                    status={
                      healthChecks.length === 0 ||
                      environmentStatus !== WorkspaceStatus.Running
                        ? "unknown"
                        : getTunnelApplicationHealthCheck(healthChecks, app)
                    }
                    environment={environment}
                    index={index + "-http"}
                    application={app}
                    port={app.port}
                    url={"https://" + app.hostname}
                    onDelete={async () => {
                      if (isDeletingEnvironment) return;
                      setIsDeletingIndex(app.name);
                      await deleteApplication(app.name);
                      setIsDeletingIndex(null);
                    }}
                  />
                ));
              })()}
          </NodeDetailTableBody>
        </NodeDetailTable>
      </>
    </>
  );
};

export default CloudflareHTTPPorts;
